import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ScaledStage } from '@/components/canvas/ScaledStage';
import type { Annotation } from '@/lib/schema.types';
import { useEditorStore } from '@/store/editor.store';

import { Stage } from './Stage';

interface Props {
  onClose: () => void;
}

/**
 * Full-screen, chrome-free preview that plays the whole pack the way the
 * Electron classroom app does: each slide is a fixed 16:9 frame scaled to fill
 * the screen (edge-to-edge on a 1920×1080 display), reveal steps are walked one
 * at a time (revealing elements + annotations and playing their audio), then it
 * advances to the next slide.
 *
 * Navigation: → / Space = next step, ← = previous, Esc = exit. This is what a
 * teacher sees to judge the lesson before publishing — no editing affordances.
 */
export function PresentMode({ onClose }: Props) {
  const slides = useEditorStore((s) => s.script?.slides ?? []);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [slideIndex, setSlideIndex] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);

  const slide = slides[Math.min(slideIndex, slides.length - 1)] ?? null;
  const steps = useMemo(() => slide?.revealSteps ?? [], [slide]);
  const hasSteps = steps.length > 0;

  // Cumulative reveal up to the current step. A slide with no reveal steps
  // shows everything at once (revealedElementIds === null).
  const revealedElementIds: Set<string> | null = useMemo(() => {
    if (!hasSteps) return null;
    const set = new Set<string>();
    for (let i = 0; i <= stepIndex && i < steps.length; i++) {
      (steps[i].elementIds ?? []).forEach((id) => set.add(id));
    }
    return set;
  }, [hasSteps, stepIndex, steps]);

  const annotations: Annotation[] = useMemo(() => {
    if (!hasSteps) return [];
    const out: Annotation[] = [];
    for (let i = 0; i <= stepIndex && i < steps.length; i++) {
      (steps[i].annotations ?? []).forEach((a) => out.push(a));
    }
    return out;
  }, [hasSteps, stepIndex, steps]);

  const lastStep = hasSteps ? steps.length - 1 : 0;
  const lastSlide = slides.length - 1;
  const atStart = slideIndex === 0 && stepIndex === 0;
  const atEnd = slideIndex >= lastSlide && stepIndex >= lastStep;

  const next = useCallback(() => {
    setStepIndex((step) => {
      if (step < lastStep) return step + 1;
      // Past the last step → advance to the next slide's first step.
      if (slideIndex < lastSlide) {
        setSlideIndex((s) => s + 1);
        return 0;
      }
      return step; // already at the very end
    });
  }, [lastStep, lastSlide, slideIndex]);

  const prev = useCallback(() => {
    setStepIndex((step) => {
      if (step > 0) return step - 1;
      if (slideIndex > 0) {
        const prevSlide = slides[slideIndex - 1];
        setSlideIndex((s) => s - 1);
        return Math.max(0, (prevSlide?.revealSteps?.length ?? 1) - 1);
      }
      return 0;
    });
  }, [slideIndex, slides]);

  // Keyboard driver + lock body scroll while presenting.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, next, prev]);

  // Play the current step's audio when it changes.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const src = hasSteps ? steps[Math.min(stepIndex, lastStep)]?.audio?.src : undefined;
    if (src) {
      el.src = src;
      el.currentTime = 0;
      void el.play().catch(() => {
        /* autoplay may be blocked — teacher can still advance manually */
      });
    } else {
      el.pause();
    }
  }, [hasSteps, steps, stepIndex, lastStep]);

  if (!slide) return null;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black">
      {/* The slide fills the viewport width (edge-to-edge on a 1920×1080
          classroom screen) and scrolls vertically if it's taller than 16:9. */}
      <div className="relative flex-1 overflow-y-auto scroll-thin">
        <ScaledStage>
          <Stage
            slide={slide}
            annotations={annotations}
            revealedElementIds={revealedElementIds}
            tool="cursor"
            onPickElement={() => {}}
          />
        </ScaledStage>
      </div>

      {/* Exit — fixed so it stays put while the slide scrolls. */}
      <button
        onClick={onClose}
        className="fixed right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white/80 backdrop-blur transition hover:bg-white/20 hover:text-white"
        title="Exit preview (Esc)"
      >
        <X size={16} />
      </button>

      {/* Control bar */}
      <div className="flex items-center justify-center gap-4 bg-black px-6 py-3 text-white">
        <button
          onClick={prev}
          disabled={atStart}
          className="grid h-9 w-9 place-items-center rounded-full bg-white/10 transition hover:bg-white/20 disabled:opacity-30"
          title="Previous (←)"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="min-w-[180px] text-center text-[12px] font-medium tracking-wide text-white/80">
          Slide {slideIndex + 1} / {slides.length}
          {hasSteps && (
            <span className="text-white/50">
              {' '}
              · step {Math.min(stepIndex + 1, steps.length)} / {steps.length}
            </span>
          )}
        </div>

        <button
          onClick={next}
          disabled={atEnd}
          className="grid h-9 w-9 place-items-center rounded-full bg-white/10 transition hover:bg-white/20 disabled:opacity-30"
          title="Next (→)"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <audio ref={audioRef} className="hidden" />
    </div>
  );
}
